import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LOS_GRID_EVENT } from "../Lit/acc-wc-views/src/acc-wc-view-los-indented/enum.js";
import { storedLocale } from "../Authentication/util";
import { mapAttributes } from "../../common/util/config";
import { useAppDispatch, useAppSelector } from "../../store";
import { setEdlosAboMeta, showProfile } from "../../reducers/profile";
import { SELECTOR_TYPE, updateSelectorType } from "../../reducers/period";
import { ROUTE_PATHS } from "../../common/enums/routing-enums";
import { EVENTS } from "../../common/enums/events";
import defaultContent from "../../content/edlos";
import { PageTitleSection } from "../../common/components/PageTitleSection/PageTitleSection";
import { ErrorTile } from "../../common/components/ErrorTile/ErrorTile";
import { ErrorPage, ERROR_TYPE } from "../ErrorPage/ErrorPage";
import "./EdLos.scss";
import LeafRectangleLoader from "../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { BonusInfoFooter } from "../../common/components/BonusInfoFooter/BonusInfoFooter";
import { FullPageComponentLoader } from "../../common/components/FullPageComponentLoader/FullPageComponentLoader";
import { useImportLitComponent, LitComponent } from "../../common/hooks/useImportLitComponent";
import { useLitRouterPopstateGuard } from "../../common/hooks/useLitRouterPopstateGuard";
import { basePath as losEdBasePath } from "../Lit/acc-wc-los-ed/src/routes/path.js";
import { getCurrentPeriod } from "../../common/util/period";
import { LOSExportModal } from "../ServerSideExport/LOSExportModal/LOSExportModal";
import ServerSideExportCompletedNotify from "../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import ServerSideExportInProgressNotify from "../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import { OpenExportV2ModalEvent } from "../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { EDLOS_ABO_META_EVENT } from "../Lit/acc-wc-los-ed/src/enum.js";

export default function EdLos() {
  const { t } = useTranslation(["edlos"]);
  const { configuration } = useAppSelector((state) => state.boot);
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const dispatch = useAppDispatch();
  const marketConfig = configuration?.edLos?.marketConfiguration;
  const removePeriodSelectorFromRoute = configuration?.removePeriodSelectorFromRoute ?? [];
  const isPeriodSelectorHidden = removePeriodSelectorFromRoute.includes(ROUTE_PATHS.EDLOS);
  const currentPeriod = useMemo(() => getCurrentPeriod(), []);
  const effectivePeriod = isPeriodSelectorHidden ? currentPeriod : selectedPeriod;
  const { isImported } = useImportLitComponent({ component: LitComponent.EDLOS });
  const [requestFailed, setRequestFailed] = useState(false);
  const [errorReloading, setErrorReloading] = useState(false);
  const [isUnauthError, setIsUnauthError] = useState(false);
  const selectedAffAbo = useAppSelector((state) => state.downlineProfile.affAbo);
  const { isFromEdLos, isShowDownlineProfile } = useAppSelector((state) => state.profile);

  //Fetches edlos metadata from lit
  useEffect(() => {
    if (!isFromEdLos || !isShowDownlineProfile || !selectedAffAbo) {
      return;
    }

    const requestId = crypto.randomUUID();

    const handleAboMeta = (event: CustomEvent) => {
      if (event.detail?.requestId !== requestId) return;

      dispatch(setEdlosAboMeta(event.detail.edlosAboMeta));
    };

    window.addEventListener(EDLOS_ABO_META_EVENT.RESPONSE, handleAboMeta as EventListener);

    // Prevent the previous ABO's metadata from being displayed while resolving.
    dispatch(setEdlosAboMeta(null));

    document.dispatchEvent(
      new CustomEvent(EDLOS_ABO_META_EVENT.REQUEST, {
        detail: {
          requestId,
          affAbo: selectedAffAbo,
        },
      }),
    );

    return () => {
      window.removeEventListener(EDLOS_ABO_META_EVENT.RESPONSE, handleAboMeta as EventListener);
    };
  }, [dispatch, isFromEdLos, isShowDownlineProfile, selectedAffAbo]);

  useEffect(() => {
    dispatch(updateSelectorType(SELECTOR_TYPE.MONTHLY));
  }, [dispatch]);

  useEffect(() => {
    if (isImported) {
      const edlos = document.getElementById("acc_wc_los_ed_component");
      if (!edlos) return;

      if (!configuration.edLos) return;
      mapAttributes(edlos, configuration.edLos, {});

      const handleLitError = (event: CustomEvent) => {
        setIsUnauthError(event.detail === "unauth");
        setRequestFailed(true);
      };

      edlos.addEventListener("acc-wc-los-ed-error", handleLitError as EventListener);

      return () => {
        edlos.removeEventListener("acc-wc-los-ed-error", handleLitError as EventListener);
      };
    }
    // requestFailed unmounts/remounts <acc-wc-los-ed> (conditional render below), which
    // creates a new DOM node that needs the listener reattached
  }, [isImported, configuration.edLos, requestFailed]);

  useEffect(() => {
    setRequestFailed(false);
    setIsUnauthError(false);
    setErrorReloading(false);
  }, [effectivePeriod]);

  // Dispatch period-changed event so nested Lit components (e.g., acc-wc-los-ar
  // inside Downline LOS) pick up the new period without remounting.
  // This follows the same pattern used in RwLos.tsx.
  useEffect(() => {
    document.dispatchEvent(
      new CustomEvent("period-changed", {
        bubbles: true,
        composed: true,
        detail: { period: effectivePeriod },
      }),
    );
  }, [effectivePeriod]);

  const [openExportModal, setExportModalState] = useState(false);

  //controlling the new abo profile event on main edlos
  const showABOProfileEd = useCallback(
    (event: CustomEventInit) => {
      const showOnlyPerformanceHistory = event.detail.select === "historyGroup";
      dispatch(
        showProfile({
          affAbo: event.detail.abo.affAbo,
          previousElement: event.detail.target,
          showOnlyPerformanceHistory,
          isFromEdLos: true,
          edlosAboMeta: {
            level: event.detail.abo.level,
            enableDownlineLOS: event.detail.abo.enableDownlineLOS,
          },
        }),
      );
    },
    [dispatch],
  );
  //controlling the new abo profile event for downline los
  const showABOProfileDownline = useCallback(
    (event: CustomEventInit) => {
      // market config == 1 is full los view and market config == 2 is restricted view, only appicable on downline los
      let showOnlyPerformanceHistory = false;
      switch (marketConfig) {
        case 1:
          showOnlyPerformanceHistory = false;
          break;
        case 2:
          showOnlyPerformanceHistory = true;
          break;
        default:
          return;
      }
      dispatch(
        showProfile({
          affAbo: event.detail.abo.affAbo,
          previousElement: event.detail.target,
          showOnlyPerformanceHistory,
          isFromEdLos: true,
          edlosRootAffAbo: event.detail.topLevelAffAbo,
          edlosRootDepth: event.detail.topLevelDepth,
          edlosAboMeta: {
            level: event.detail.abo.level,
            enableDownlineLOS: true,
          },
        }),
      );
    },
    [dispatch, marketConfig],
  );

  useEffect(() => {
    window.addEventListener(LOS_GRID_EVENT.ACTION_SEND_ABO, showABOProfileDownline);
    window.addEventListener("acc-wc-los-ed-indented-action-profile", showABOProfileEd);

    return () => {
      window.removeEventListener(LOS_GRID_EVENT.ACTION_SEND_ABO, showABOProfileDownline);
      window.removeEventListener("acc-wc-los-ed-indented-action-profile", showABOProfileEd);
    };
  }, [showABOProfileDownline, showABOProfileEd]);

  // Bridges "View Their LOS" clicked from the Downline Profile modal (when that
  // profile was opened from EdLOS) into the same T&C-gated navigation the EdLOS
  // grid's own "View Downline LOS" button uses, instead of duplicating the
  // agreement check in React.
  const handleViewDownlineLosFromProfile = useCallback((event: CustomEventInit) => {
    const indented = document
      .getElementById("acc_wc_los_ed_component")
      ?.shadowRoot?.querySelector("main")
      ?.querySelector("acc-wc-los-ed-indented") as
      | (HTMLElement & { viewDownlineLosFromExternal?: (abo: unknown) => void })
      | null;
    indented?.viewDownlineLosFromExternal?.(event.detail);
  }, []);

  useEffect(() => {
    window.addEventListener(
      EVENTS.EVENT_LISTENER.EDLOS_VIEW_DOWNLINE_LOS_FROM_PROFILE,
      handleViewDownlineLosFromProfile,
    );
    return () => {
      window.removeEventListener(
        EVENTS.EVENT_LISTENER.EDLOS_VIEW_DOWNLINE_LOS_FROM_PROFILE,
        handleViewDownlineLosFromProfile,
      );
    };
  }, [handleViewDownlineLosFromProfile]);

  const showExportModal = useCallback((event: CustomEventInit) => {
    setExportModalState(Boolean(event.detail?.open));
  }, []);

  useEffect(() => {
    window.addEventListener(OpenExportV2ModalEvent, showExportModal);
    return () => {
      window.removeEventListener(OpenExportV2ModalEvent, showExportModal);
    };
  }, [showExportModal]);

  const notifyDownloadLinkOnClick = () => {
    setExportModalState(true);
  };

  const closeExportModal = () => {
    setExportModalState(false);
  };

  useLitRouterPopstateGuard(losEdBasePath, ROUTE_PATHS.EDLOS);

  const pageTitle = t("pageTitle", defaultContent["pageTitle"]);

  if (!isImported) {
    return <FullPageComponentLoader />;
  }

  const reloadEdLos = () => {
    setErrorReloading(true);
    window.location.reload();
  };

  const getErrorComponent = () => {
    if (isUnauthError) {
      return (
        <ErrorPage
          errorType={ERROR_TYPE.INCENTIVE_UNAVAILABLE_FOR_PERIOD}
          pageTitle={pageTitle}
          containerClassName="EdLos"
        />
      );
    }
    return errorReloading ? (
      <div className="EdLos__custom-error EdLos__custom-error--loading">
        <LeafRectangleLoader isLoading={true} width={"100%"} height={"14rem"} />
      </div>
    ) : (
      <div className="EdLos EdLos__custom-error">
        <ErrorTile
          clickFunction={reloadEdLos}
          errorStatus={true}
          isLoading={false}
          errorMessage={`${t("timeoutError", defaultContent["timeoutError"])}`}
          btnText={`${t("refresh", defaultContent["refresh"])}`}
          tealiumTrackId="ED_LOS"
        />
      </div>
    );
  };

  // When unauth error occurs, we return the full error page (not wrapped in the normal layout)
  if (requestFailed && isUnauthError) {
    return getErrorComponent();
  }

  return (
    <main>
      <PageTitleSection title={pageTitle} hideTimeStamp />
      <div className="EdLos__wrapper">
        {requestFailed && !isUnauthError ? (
          getErrorComponent()
        ) : (
          <acc-wc-los-ed
            id="acc_wc_los_ed_component"
            mode="standalone"
            locale={storedLocale()}
            period={effectivePeriod}
          ></acc-wc-los-ed>
        )}
      </div>

      <BonusInfoFooter hideDate />
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify moduleId={"los"} notifyDownloadLinkOnClick={notifyDownloadLinkOnClick} />
      <LOSExportModal open={openExportModal} onCloseCallbackFn={closeExportModal} view="edLos" />
    </main>
  );
}
